<script setup lang="ts">
/**
 * A reporting error boundary with a fallback (UX roadmap #17).
 *
 * App.vue's onErrorCaptured caught every error and returned false with nothing in its place, so a component that threw
 * while rendering just vanished: a product page kept only its logo (ProductContextsPlugin), the sector flyout stayed on
 * '---' (DP-01), a dashboard widget went missing, and nothing offered a retry. Wrapped in this, a section that fails to
 * render shows AsyncState's error box ("This section couldn't load." and a "Try again" that mounts it afresh).
 *
 * Every error captured here is reported through queueClientError and stops here. Only one that leaves the section
 * with nothing to show (thrown in setup or render; see breaksRendering) swaps it for the fallback: a failed click
 * handler or watcher is reported and the section stays as it is.
 *
 * `resetKey` clears the fallback when it changes (the route's path, the selected tab), so moving on from a broken
 * section does not leave its fallback behind. It never remounts a section that did not fail.
 */
import { onErrorCaptured, ref, shallowRef, watch } from "vue";
import type { ComponentPublicInstance } from "vue";

import AsyncState from "@/components/common/AsyncState.vue";
import { breaksRendering, reportCapturedError } from "@/composables/useCapturedErrors";

const props = withDefaults( defineProps< {
  /** Names the boundary in the error report: 'page', 'product-tab', 'dashboard-widget', … */
  name: string;
  /** A change clears the fallback, e.g. the route's path. */
  resetKey?: unknown;
  title?: string;
  hint?: string;
  /** Prefix of the fallback's test id: `<testid>-error`. */
  testid?: string;
} >(), {
  resetKey: undefined,
  title: "This section couldn't load.",
  hint: 'Something went wrong while showing it. Try again, or reload the page if it keeps happening.',
  testid: 'error-boundary'
} );

// The section's root is a fragment, which cannot take a class; style a wrapper inside the slot instead
defineOptions( { inheritAttrs: false } );

const failed = shallowRef< boolean >( false );
// Keys the section: "Try again" mounts it from scratch rather than patching the tree that failed
const generation = ref< number >( 0 );

onErrorCaptured( ( err: unknown, instance: ComponentPublicInstance | null, info: string ) => {
  reportCapturedError( err, instance, info, props.name );

  // The error itself never reaches AsyncState: a render error is usually a TypeError, which it would read as "offline"
  if ( !failed.value && breaksRendering( info ) ) {
    failed.value = true;
  }

  // Reported here: App.vue's last-resort hook must not report it again
  return false;
} );

function reset(): void {
  failed.value = false;
  generation.value++;
}

watch( () => props.resetKey, () => {
  if ( failed.value ) {
    reset();
  }
} );

defineExpose( { reset } );
</script>

<template>
  <AsyncState v-if="failed" status="error" :error-title="title" :error-hint="hint" :testid="testid" @retry="reset" />
  <template v-else :key="generation">
    <slot></slot>
  </template>
</template>
